/**
 * DDL-FOLLOWUP-IDREF — resolve a report's documents through the canonical
 * `site.documents` array rather than the legacy per-report snapshot.
 *
 * Background:
 *   Historically `SiteRagReport.documents: SiteDocument[]` carried a full
 *   snapshot of every consumed doc at generation time. When the canonical
 *   `site.documents` array later mutated (rename, soft-delete, re-extraction,
 *   importance recompute), the report's snapshot drifted — producing the
 *   DRC/DDL divergence class the platform spent multiple packs patching.
 *
 *   The IDREF refactor closes the divergence at the source by storing only
 *   `report.documentIds: string[]` and resolving against `site.documents`
 *   at render time. Live updates flow through automatically; rename a doc
 *   on the site and every historical report's view of it reflects the new
 *   name without a write.
 *
 * Resolution contract:
 *   1. If `report.documentIds` is set AND non-empty → resolve each id
 *      against `site.documents`. Missing ids (deleted from the site since
 *      the report ran) are filtered out silently. Order follows
 *      `documentIds` (= insertion order at gen time).
 *   2. Else (legacy report pre-IDREF) → return `report.documents ?? []`
 *      verbatim. Back-compat is mandatory — every report already in
 *      Firestore must continue rendering. The legacy snapshot is the
 *      ground truth for those rows.
 *
 *   This contract composes with `DDL-FOLLOWUP-CONFIRM-DIALOG` (shipping
 *   in parallel): a soft-deleted doc (one carrying `deletedAt`) on the
 *   site disappears from the resolved view because either (a) the doc
 *   has been removed from `site.documents` entirely or (b) callers can
 *   defensively pre-filter on `deletedAt` before invoking the resolver.
 *   The resolver itself stays format-agnostic — it just resolves ids
 *   against whatever `site.documents` looks like today.
 *
 * Composition with the older `getReportDocuments` helper:
 *   `getReportDocuments` (reportDocs.ts) routes through `documentRefs`
 *   which carries the richer per-ref classifier metadata
 *   (relevance / userIncluded / reasonIfExcluded). `resolveSiteDocuments`
 *   is the simpler "what's the live set of docs this report consumes?"
 *   accessor for surfaces that don't need the per-ref policy bits.
 *   Both helpers can co-exist; `getReportDocuments` will migrate to
 *   route through `documentIds` once a soak period confirms the new
 *   field is universally stamped.
 *
 * Purity:
 *   - Never mutates `site` or `report`.
 *   - Never throws (handles `null` / `undefined` site, empty arrays).
 *   - Memo-friendly at React callers — keyed on `report` / `site`
 *     identity.
 */
import type { SiteRagReport, SiteDocument } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';

/**
 * Resolve a report's effective document list against the live site.
 *
 * @param site   Owning site. May be `undefined` during early renders /
 *               unit-tests that don't have a site available — in that
 *               case the resolver falls back to the legacy snapshot.
 * @param report Report to resolve documents for.
 * @returns      Insertion-ordered `SiteDocument[]`. Missing ids are
 *               filtered (the site has since lost the doc). Never
 *               returns `undefined`.
 */
/**
 * Resolve a non-blank display/dedupe fileName at the read/presentation boundary:
 * 1. Canonical non-blank `doc.fileName`
 * 2. Supported legacy non-blank `doc.name`
 * 3. Stable per-document identity (`doc.id` or caller-supplied index fallback)
 * Never returns a shared blank string that would collapse unrelated unnamed documents.
 */
const FALLBACK_DOCUMENT_IDENTITY_SYMBOL = Symbol.for('datacrate.fallbackDocumentIdentity');

export interface ResolvedSiteDocumentNameInfo {
  fileName: string;
  isFallbackIdentity: boolean;
}

export function resolveSiteDocumentNameInfo(
  doc: (Partial<SiteDocument> & { name?: unknown }) | null | undefined,
  fallbackId?: string,
): ResolvedSiteDocumentNameInfo {
  const existingFallback =
    doc && typeof doc === 'object'
      ? (doc as Record<symbol, unknown>)[FALLBACK_DOCUMENT_IDENTITY_SYMBOL]
      : undefined;
  if (typeof existingFallback === 'string' && existingFallback.length > 0) {
    return { fileName: existingFallback, isFallbackIdentity: true };
  }
  if (doc && typeof doc.fileName === 'string' && doc.fileName.trim().length > 0) {
    return { fileName: doc.fileName.trim(), isFallbackIdentity: false };
  }
  const legacyName = doc && typeof doc.name === 'string' ? doc.name.trim() : '';
  if (legacyName.length > 0) {
    return { fileName: legacyName, isFallbackIdentity: false };
  }
  const docId = doc && typeof doc.id === 'string' ? doc.id.trim() : '';
  if (docId.length > 0) {
    return { fileName: docId, isFallbackIdentity: true };
  }
  if (typeof fallbackId === 'string' && fallbackId.trim().length > 0) {
    return { fileName: fallbackId.trim(), isFallbackIdentity: true };
  }
  return { fileName: 'untitled-document', isFallbackIdentity: true };
}

export function resolveSiteDocumentFileName(
  doc: (Partial<SiteDocument> & { name?: unknown }) | null | undefined,
  fallbackId?: string,
): string {
  return resolveSiteDocumentNameInfo(doc, fallbackId).fileName;
}

export function withResolvedSiteDocumentFileName(
  doc: SiteDocument,
  fallbackId?: string,
): SiteDocument {
  const info = resolveSiteDocumentNameInfo(doc as SiteDocument & { name?: unknown }, fallbackId);
  if (
    !info.isFallbackIdentity
    && typeof doc?.fileName === 'string'
    && doc.fileName.trim().length > 0
  ) {
    return doc;
  }
  const wrapped: SiteDocument = {
    ...doc,
    fileName: info.fileName,
  };
  if (info.isFallbackIdentity) {
    Object.defineProperty(wrapped, FALLBACK_DOCUMENT_IDENTITY_SYMBOL, {
      value: info.fileName,
      enumerable: false,
      configurable: true,
    });
  }
  return wrapped;
}

export function resolveSiteDocuments(
  site: SiteEntity | undefined,
  report: SiteRagReport,
): SiteDocument[] {
  // New canonical path — `documentIds` is the ground truth.
  const ids = report.documentIds;
  if (Array.isArray(ids) && ids.length > 0) {
    const siteDocs = site?.documents ?? [];
    // O(N+M) — build the by-id map once even if ids.length is large.
    const byId = new Map<string, SiteDocument>(siteDocs.map(d => [d.id, d]));
    const out: SiteDocument[] = [];
    for (let idx = 0; idx < ids.length; idx += 1) {
      const id = ids[idx];
      // Guard against malformed ids in legacy data (empty string,
      // accidental `undefined` stamped through a stripUndefined pass).
      if (typeof id !== 'string' || id.length === 0) continue;
      const doc = byId.get(id);
      if (doc) out.push(withResolvedSiteDocumentFileName(doc, `doc-${idx}`));
      // Missing id ⇒ doc deleted from the site since the report ran.
      // Filtered silently — the consumer surfaces (DRC stats, etc.)
      // are responsible for surfacing "deleted since gen" tallies via
      // a comparison between `documentIds.length` and `out.length`.
    }
    return out;
  }
  // Legacy fallback — pre-IDREF reports carried the full snapshot.
  return (report.documents ?? []).map((doc, idx) =>
    withResolvedSiteDocumentFileName(doc, `doc-${idx}`),
  );
}
